import type { LocationQuery, LocationQueryRaw } from 'vue-router';

import type { RecordFiltersType } from '@/types/individual_record';
import type { PartnerScoreBucket } from '@/types/partners';
import { ALL_SEGMENTS } from '@/types/populations';
import type { SegmentType } from '@/types/populations';

export const PARTNER_SCORE_BUCKETS = ['high', 'medium', 'low'] as const;

type PartnerScoreBucketType = (typeof PARTNER_SCORE_BUCKETS)[number];

export const PARTNER_SCORE_BUCKET_LABELS = {
  high: 'High',
  medium: 'Medium',
  low: 'Low',
} as const satisfies Record<PartnerScoreBucketType, string>;

function toStrings(value: LocationQuery[string] | undefined): string[] {
  if (value === undefined || value === null) return [];
  const values = Array.isArray(value) ? value : [value];
  return values.filter((entry): entry is string => !!entry);
}

function isSegment(value: string): value is SegmentType {
  return (ALL_SEGMENTS as readonly string[]).includes(value);
}

function isScoreBucket(
  value: string,
): value is NonNullable<PartnerScoreBucket> {
  return (PARTNER_SCORE_BUCKETS as readonly string[]).includes(value);
}

export function filtersFromQuery(query: LocationQuery): RecordFiltersType {
  return {
    partner_search: toStrings(query.partner_search)[0] ?? '',
    partner_org_ids: toStrings(query.partner_org_ids)
      .map(Number)
      .filter(Number.isInteger),
    partner_tag_ids: toStrings(query.partner_tag_ids),
    partner_segments: toStrings(query.partner_segments).filter(isSegment),
    partner_score_bucket: toStrings(query.partner_score_bucket).filter(
      isScoreBucket,
    ),
  };
}

export function filtersToQuery(
  query: LocationQuery,
  filters: Partial<RecordFiltersType>,
): LocationQueryRaw {
  const changedKeys = Object.keys(filters);
  const untouched = Object.entries(query).filter(
    ([key]) => !changedKeys.includes(key),
  );
  const applied = Object.entries(filters).filter(([, value]) =>
    Array.isArray(value) ? value.length : !!value,
  );
  return Object.fromEntries([...untouched, ...applied]);
}

export function hasFilterQueryChanged(a: LocationQuery, b: LocationQuery) {
  return (
    JSON.stringify(filtersFromQuery(a)) !== JSON.stringify(filtersFromQuery(b))
  );
}
